import React from "react";
import { Controller, Control } from "react-hook-form";

import { isEmpty } from "lodash";
import CrossIcon from "public/icons/close.svg";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
  css,
  styled,
} from "@mui/material";

import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { ConditionalWrapper } from "@/shared/ConditionalWrapper";
import { ThemeProviderWithPrimaryPink } from "@/shared/ThemeProviderWithPrimaryPink";
import { skipForwardProp } from "@/utils/skipForwardProp";

type PropTypes = SelectProps & {
  options: { label: string; value: string | number }[];
  label: string;
  control: Control;
  name: string;
  pinkVersion?: boolean;
};

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: skipForwardProp(["valueNotEmpty"]),
})<{ valueNotEmpty?: boolean }>(({ theme, valueNotEmpty }) => {
  return valueNotEmpty
    ? css`
        fieldset,
        fieldset:hover,
        .Mui-focused fieldset.MuiOutlinedInput-notchedOutline {
          border: 2px solid ${theme.palette.primary.main} !important;
          border-radius: 4px !important;
        }
      `
    : "";
});

export const Select: React.FC<PropTypes> = ({
  options,
  label,
  name,
  control,
  pinkVersion = false,
  ...rest
}) => {
  return (
    <ConditionalWrapper
      on={pinkVersion}
      Wrapper={({ children }) => {
        return (
          <ThemeProviderWithPrimaryPink>
            {children}
          </ThemeProviderWithPrimaryPink>
        );
      }}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={""}
        render={({ field: { onChange, value } }) => {
          const valueNotEmpty = !isEmpty(value);

          return (
            <StyledFormControl
              fullWidth
              valueNotEmpty={valueNotEmpty}
              data-testid={`${name}Select`}
            >
              {/* <InputLabel className={`${valueNotEmpty ? "text-blue" : ""}`}> */}
              <InputLabel>{label}</InputLabel>
              <MuiSelect
                variant="outlined"
                label={label}
                onChange={onChange}
                value={
                  options.find(({ value: _value }) => _value === value)
                    ?.value || ""
                }
                MenuProps={{ disableScrollLock: true }}
                {...rest}
                {...(valueNotEmpty
                  ? {
                      endAdornment: (
                        <SvgIcon
                          color="grey"
                          className="w-5 h-5 hover:cursor-pointer absolute right-6"
                          onClick={() => onChange("")}
                          role="button"
                          aria-label={`Clear ${label}`}
                        >
                          <CrossIcon />
                        </SvgIcon>
                      ),
                    }
                  : {})}
              >
                {options.map(({ label, value }, i) => {
                  return (
                    <MenuItem key={i} value={value} className="text-sm">
                      {label}
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </StyledFormControl>
          );
        }}
      ></Controller>
    </ConditionalWrapper>
  );
};
